"use client"

import { useQuery } from "@tanstack/react-query"

import { useAuth } from "@/hooks/use-auth"

export interface TenantListEntry {
  id: string
  name: string
  is_active?: boolean
  archived_at?: string | null
}

export const tenantsQueryKey = ["tenants", "list"] as const

/**
 * The tenant list behind the switcher — one request however many switchers are
 * mounted (one per breakpoint). Not tenant-scoped: impersonation reloads the
 * page, so the cache never outlives a switch.
 */
export function useTenantsQuery({ enabled }: { enabled: boolean }) {
  const { fetchWithAuth } = useAuth()

  return useQuery({
    queryKey: tenantsQueryKey,
    enabled,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<TenantListEntry[]> => {
      const response = await fetchWithAuth("/api/tenants")
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        throw new Error(payload.error || "Failed to load tenants")
      }
      const { tenants } = (await response.json()) as { tenants?: TenantListEntry[] }
      return tenants ?? []
    },
  })
}
